import clsx from 'clsx';
import * as React from 'react';
import { Button } from 'react-aria-components';

import type { SVGIconComponent } from '@gitbook/spine-icons';

import { ButtonLabel } from '../Button';

export const getPlaceholderStyles = (props: { isFocusVisible: boolean; isHovered: boolean }) =>
    clsx(
        'border border-dashed',
        props.isFocusVisible || props.isHovered
            ? 'border-[var(--sp-color-border-placeholder)] bg-action-active text-[var(--sp-color-text-placeholder)]'
            : 'border-control bg-base text-[var(--sp-color-text-muted)]',
        props.isFocusVisible && 'shadow-focus-ring'
    );

export const PlaceholderButton = React.forwardRef<
    HTMLButtonElement,
    {
        label: string;
        icon?: SVGIconComponent;
        size?: 'large' | 'medium' | 'small';
        onPress?: () => void;
        onDrop?: (event: React.DragEvent<HTMLButtonElement>) => void;
        className?: string;
        kind?: 'default' | 'pill' | 'ghost';
        disabled?: boolean;
        iconOnly?: boolean;
    }
>(function PlaceholderButton(
    {
        label,
        size = 'medium',
        onPress,
        icon: Icon,
        className,
        kind = 'default',
        disabled,
        iconOnly,
    },
    ref
) {
    return (
        <Button
            ref={ref}
            aria-label={label}
            onPress={onPress}
            isDisabled={disabled}
            className={(props) =>
                clsx(
                    className,
                    'fade-in flex flex-1 animate-in cursor-pointer items-center gap-2 overflow-hidden text-ellipsis whitespace-nowrap rounded transition-all',
                    'cursor-pointer text-nowrap',
                    getPlaceholderStyles(props),
                    kind === 'ghost' ? 'absolute' : 'relative',
                    {
                        'justify-center text-ui-action': kind === 'default' || kind === 'ghost',
                    },
                    {
                        'flex-shrink flex-grow-0 basis-auto px-1 py-1 text-ui-small':
                            kind === 'pill',
                    },
                    {
                        'p-2': size === 'small',
                        'm-1 p-2': size === 'medium',
                        'mx-1 mt-1 p-14': size === 'large',
                    },
                    {
                        'mx-0': kind === 'ghost',
                    }
                )
            }
            data-palette-anchor=""
        >
            {Icon && <Icon className="size-4 shrink-0" />}
            {label && !iconOnly ? (
                <ButtonLabel hasIcon={Boolean(Icon)} iconOnly={iconOnly} size={size}>
                    {label}
                </ButtonLabel>
            ) : null}
        </Button>
    );
});
